import { Helmet } from 'react-helmet-async';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Popover,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { toast } from 'react-toastify';
import Services from 'src/services/Services';

// Statü rozetlerinin renkleri
const STATUS_STYLES = {
  'Onay Bekleniyor': { color: '#B25E09', background: '#FDF3E6', dot: '#F0A02A' },
  'Reddedildi': { color: '#B32318', background: '#FCEBEA', dot: '#E5484D' },
  'Onaylandı': { color: '#175CD3', background: '#EAF2FE', dot: '#2E90FA' },
  'Bilinmiyor': { color: '#475467', background: '#F2F4F7', dot: '#98A2B3' },
};

// Servisin döndüğü statü kodları. Eşlenmemiş bir kod "Bilinmiyor" gösterilir ve
// üzerinde işlem yapılamaz; yanlış bir siparişin onaylanıp reddedilmesini önler.
const STATUS_BY_CODE = {
  1: 'Onaylandı',
  2: 'Reddedildi',
  3: 'Onay Bekleniyor',
};
const PENDING_STATUS_CODE = '3';

// Filtrede yalnızca servisin gerçekten döndürebildiği statüler listelenir.
const STATUS_OPTIONS = Object.values(STATUS_BY_CODE);

const ROWS_PER_PAGE = 10;

const TABLE_HEAD = [
  'Avm Adı',
  'Müşteri Ad',
  'Müşteri Soyad',
  'Mağaza',
  'İşlem Tarihi',
  'Teslimat Saat Aralığı',
  'Statü Durumu',
  'Detay',
  'Aksiyon',
];

const isValidID = (value) => Boolean(value) && value !== 'undefined' && value !== 'null';

// Kullanıcının AVM'si, bağlı olduğu noktanın (firstResponsibleOffice) içinde geliyor;
// panelin diğer yerleri de oradan okuyor. Girişte yazılan localStorage.mallID yedek:
// yoksa oraya "undefined" metni yazıldığı için o da boş sayılır.
function getMallID() {
  try {
    const officeMallID = JSON.parse(localStorage.getItem('user'))?.firstResponsibleOffice?.mallID;
    if (isValidID(String(officeMallID ?? ''))) return String(officeMallID);
  } catch (error) {
    // Bozuk kullanıcı kaydı: yedeğe düş.
  }
  const value = localStorage.getItem('mallID');
  return isValidID(value) ? value : '';
}

// Servis tarihleri Unix saniye olarak dönüyor.
function formatUnix(seconds) {
  const value = Number(seconds);
  if (!value) return '-';
  return moment.unix(value).format('DD.MM.YYYY HH:mm');
}

function getReceiptSource(image) {
  if (typeof image === 'string') return image;
  if (image?.file) return `data:image/${image.extension || 'jpeg'};base64,${image.file}`;
  return '';
}

function mapOrderToRow(order) {
  const code = String(order.status);
  const status = STATUS_BY_CODE[code] || 'Bilinmiyor';

  let decidedAt = '-';
  if (code === '2') decidedAt = formatUnix(order.cancelledDateTime);
  if (code === '1') {
    decidedAt = formatUnix(order.store_confirmed_date_time || order.customer_confirmed_date_time);
  }

  return {
    id: order.id ?? order.orderID,
    orderID: order.orderID,
    avm: order.mallTitle || '-',
    name: order.customerName || '-',
    lastname: order.customerLastname || '-',
    store: order.branchTitle || '-',
    createdAt: Number(order.createdDateTime) || null,
    date: formatUnix(order.createdDateTime),
    slot: order.privateDeliveryRequestedTimeRange || '-',
    status,
    isPending: code === PENDING_STATUS_CODE,
    decidedAt,
    // Kurye teslim tarihi servis yanıtında yok; eklendiğinde buradan doldurulacak.
    courierDeliveredAt: '-',
    customerDeliveredAt: formatUnix(order.completed_date_time),
    address: order.deliveryTypeInfo?.text || '-',
    receipts: (order.invoiceImages || [])
      .map((image, index) => ({ name: `Fiş ${index + 1}`, src: getReceiptSource(image) }))
      .filter((receipt) => receipt.src),
  };
}

// 1 2 3 ... 8 9 10 biçiminde sayfa numaraları
function getPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);
  if (current <= 3) return [1, 2, 3, 4, '...', total - 1, total];
  if (current >= total - 2) return [1, 2, '...', total - 3, total - 2, total - 1, total];
  return [1, '...', current - 1, current, current + 1, '...', total];
}

async function getHandsFreeToken() {
  const response = await Services.GetHandsFreeToken();
  const token = response?.data?.result?.token;
  if (!token) {
    throw new Error(response?.data?.message || 'Hands Free oturumu açılamadı.');
  }
  return token;
}

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

function StatusChip({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.Bilinmiyor;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{
        display: 'inline-flex',
        backgroundColor: style.background,
        color: style.color,
        borderRadius: 1,
        px: 1.25,
        py: 0.5,
        fontSize: 13,
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: style.dot }} />
      <span>{status}</span>
    </Stack>
  );
}

function DetailRow({ label, value }) {
  return (
    <Box sx={{ py: 1.5, borderBottom: '1px solid #EDEDED' }}>
      <Typography component="span" sx={{ fontWeight: 700 }}>
        {label}:
      </Typography>{' '}
      <Typography component="span">{value}</Typography>
    </Box>
  );
}

const rejectButtonSx = {
  backgroundColor: '#D92D20',
  boxShadow: 'none',
  '&:hover': { backgroundColor: '#B42318', boxShadow: 'none' },
};

const approveButtonSx = {
  backgroundColor: '#1E7A45',
  boxShadow: 'none',
  '&:hover': { backgroundColor: '#17603A', boxShadow: 'none' },
};

export default function HandsFreeOperations() {
  const [mallID] = useState(getMallID);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [detailRow, setDetailRow] = useState(null);
  const [previewReceipt, setPreviewReceipt] = useState(null);

  // Onay/red öncesi soru modalı ve istek sürerken kilit
  const [pendingAction, setPendingAction] = useState(null);
  const [acting, setActing] = useState(false);

  // Tarih aralığı: seçim popover içinde tutulur, "Uygula" ile filtreye yansır.
  const [dateAnchor, setDateAnchor] = useState(null);
  const [draftStart, setDraftStart] = useState(null);
  const [draftEnd, setDraftEnd] = useState(null);
  const [range, setRange] = useState({ start: null, end: null });

  const fetchOrders = useCallback(async () => {
    if (!mallID) {
      setRows([]);
      setLoadError('Hesabınıza bağlı AVM bilgisi bulunamadı.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError('');
    try {
      const token = await getHandsFreeToken();
      const response = await Services.GetMallsPrivateDeliveryOrders(token, mallID);
      if (response?.data?.status === false) {
        throw new Error(response.data.message || 'Siparişler alınamadı.');
      }
      const orders = Array.isArray(response?.data?.result) ? response.data.result : [];
      setRows(
        orders
          .map(mapOrderToRow)
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
      );
    } catch (error) {
      console.error('Hands Free siparişleri alınamadı:', error);
      setRows([]);
      setLoadError(getErrorMessage(error, 'Siparişler alınamadı.'));
    } finally {
      setLoading(false);
    }
  }, [mallID]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('tr');
    const start = range.start ? moment(range.start).startOf('day') : null;
    const end = range.end ? moment(range.end).endOf('day') : null;

    return rows.filter((row) => {
      const byStatus = statusFilter ? row.status === statusFilter : true;
      const byQuery = query
        ? [row.avm, row.name, row.lastname, row.store].some((field) =>
            String(field).toLocaleLowerCase('tr').includes(query)
          )
        : true;

      let byDate = true;
      if (start || end) {
        if (!row.createdAt) {
          byDate = false;
        } else {
          const rowDate = moment.unix(row.createdAt);
          if (start && rowDate.isBefore(start)) byDate = false;
          if (end && rowDate.isAfter(end)) byDate = false;
        }
      }

      return byStatus && byQuery && byDate;
    });
  }, [rows, statusFilter, search, range]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / ROWS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pagedRows = filteredRows.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE);
  const pageNumbers = getPageNumbers(currentPage, totalPages);

  const rangeLabel = range.start || range.end
    ? `${range.start ? moment(range.start).format('DD/MM/YYYY') : '…'} - ${range.end ? moment(range.end).format('DD/MM/YYYY') : '…'}`
    : 'Tarih Aralığı';

  const openDatePopover = (event) => {
    setDraftStart(range.start);
    setDraftEnd(range.end);
    setDateAnchor(event.currentTarget);
  };

  const applyRange = () => {
    setRange({ start: draftStart, end: draftEnd });
    setDateAnchor(null);
    setPage(1);
  };

  const clearRange = () => {
    setDraftStart(null);
    setDraftEnd(null);
    setRange({ start: null, end: null });
    setDateAnchor(null);
    setPage(1);
  };

  const requestAction = (row, type) => setPendingAction({ row, type });

  // Onay ya da red: işlem bitene kadar modal açık ve butonlar kilitli kalır. Başarılıysa
  // liste yeniden çekilir; böylece onay/red tarihi gibi alanlar servisten güncel gelir.
  const handleConfirmAction = async () => {
    if (!pendingAction) return;
    const { row, type } = pendingAction;
    const isApprove = type === 'approve';

    setActing(true);
    try {
      const token = await getHandsFreeToken();
      const response = isApprove
        ? await Services.ConfirmHandsFreeOrder(token, row.orderID)
        : await Services.RejectHandsFreeOrder(token, row.orderID);

      if (response?.data?.status === false) {
        throw new Error(response.data.message);
      }

      toast.success(isApprove ? 'Talep onaylandı.' : 'Talep reddedildi.');
      setPendingAction(null);
      setDetailRow(null);
      fetchOrders();
    } catch (error) {
      console.error('Hands Free işlemi başarısız:', error);
      toast.error(getErrorMessage(error, isApprove ? 'Talep onaylanamadı.' : 'Talep reddedilemedi.'));
    } finally {
      setActing(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Hands Free İşlemleri</title>
      </Helmet>

      <Stack sx={{ px: 3 }}>
        <Typography variant="h4" sx={{ mb: 4 }}>
          Hands Free İşlemleri
        </Typography>

        <Card sx={{ p: 3 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography variant="h6">Hands Free İşlemleri</Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={openDatePopover}
                startIcon={<Icon icon="mdi:calendar-blank-outline" width={20} height={20} />}
                endIcon={<Icon icon="mdi:chevron-down" width={20} height={20} />}
                sx={{
                  height: 44,
                  minWidth: 170,
                  justifyContent: 'space-between',
                  color: '#344054',
                  whiteSpace: 'nowrap',
                }}
              >
                {rangeLabel}
              </Button>

              <Popover
                open={Boolean(dateAnchor)}
                anchorEl={dateAnchor}
                onClose={() => setDateAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { p: 2.5, mt: 1 } }}
              >
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <DatePicker
                      label="Başlangıç"
                      format="DD/MM/YYYY"
                      value={draftStart}
                      onChange={(value) => {
                        setDraftStart(value);
                        if (draftEnd && value && moment(value).isAfter(draftEnd)) {
                          setDraftEnd(null);
                        }
                      }}
                    />
                    <DatePicker
                      label="Bitiş"
                      format="DD/MM/YYYY"
                      value={draftEnd}
                      minDate={draftStart || undefined}
                      onChange={(value) => setDraftEnd(value)}
                    />
                  </Stack>
                </LocalizationProvider>

                <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 2.5 }}>
                  <Button variant="outlined" color="inherit" onClick={clearRange} sx={{ color: '#344054' }}>
                    Temizle
                  </Button>
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={applyRange}
                    sx={{ boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
                  >
                    Uygula
                  </Button>
                </Stack>
              </Popover>

              <TextField
                select
                size="small"
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  setPage(1);
                }}
                SelectProps={{ displayEmpty: true }}
                sx={{ minWidth: 180, '& .MuiInputBase-root': { height: 44 } }}
              >
                <MenuItem value="">Statü Seçimi</MenuItem>
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                size="small"
                placeholder="Hızlı arama..."
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Icon icon="eva:search-fill" width={20} height={20} color="#98A2B3" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 240, '& .MuiInputBase-root': { height: 44 } }}
              />
            </Stack>
          </Stack>

          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  {TABLE_HEAD.map((label) => (
                    <TableCell key={label} sx={{ whiteSpace: 'nowrap' }}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                )}

                {!loading && loadError && (
                  <TableRow>
                    <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 6 }}>
                      <Typography sx={{ color: '#B42318', mb: mallID ? 2 : 0 }}>{loadError}</Typography>
                      {mallID && (
                        <Button variant="outlined" color="inherit" onClick={fetchOrders} sx={{ color: '#344054' }}>
                          Tekrar Dene
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )}

                {!loading && !loadError && pagedRows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.avm}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.lastname}</TableCell>
                    <TableCell>{row.store}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.date}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.slot}</TableCell>
                    <TableCell>
                      <StatusChip status={row.status} />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        onClick={() => setDetailRow(row)}
                        sx={{ color: '#344054', minWidth: 90 }}
                      >
                        Detay
                      </Button>
                    </TableCell>
                    <TableCell>
                      {row.isPending && (
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="contained"
                            size="small"
                            disableElevation
                            onClick={() => requestAction(row, 'reject')}
                            sx={{ minWidth: 84, ...rejectButtonSx }}
                          >
                            Reddet
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            disableElevation
                            onClick={() => requestAction(row, 'approve')}
                            sx={{ minWidth: 84, ...approveButtonSx }}
                          >
                            Onayla
                          </Button>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}

                {!loading && !loadError && pagedRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 6, color: '#667085' }}>
                      Kayıt bulunamadı
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 3, pt: 2, borderTop: '1px solid #EAECF0' }}
          >
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<Icon icon="mdi:arrow-left" width={18} height={18} />}
              disabled={currentPage === 1}
              onClick={() => setPage(Math.max(1, currentPage - 1))}
              sx={{ color: '#344054' }}
            >
              Geri
            </Button>

            <Stack direction="row" spacing={0.5} alignItems="center">
              {pageNumbers.map((item, index) =>
                item === '...' ? (
                  <Typography key={`ellipsis-${index}`} sx={{ px: 1, color: '#667085' }}>
                    ...
                  </Typography>
                ) : (
                  <Button
                    key={item}
                    onClick={() => setPage(item)}
                    sx={{
                      minWidth: 36,
                      height: 36,
                      color: currentPage === item ? '#101828' : '#667085',
                      backgroundColor: currentPage === item ? '#F2F4F7' : 'transparent',
                      fontWeight: currentPage === item ? 600 : 400,
                    }}
                  >
                    {item}
                  </Button>
                )
              )}
            </Stack>

            <Button
              variant="outlined"
              color="inherit"
              endIcon={<Icon icon="mdi:arrow-right" width={18} height={18} />}
              disabled={currentPage === totalPages}
              onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
              sx={{ color: '#344054' }}
            >
              İleri
            </Button>
          </Stack>
        </Card>
      </Stack>

      <Dialog open={Boolean(detailRow)} onClose={() => setDetailRow(null)} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 4 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <Typography variant="h6" sx={{ mb: 1 }}>
              Hands Free İşlem Detayı
            </Typography>
            <IconButton onClick={() => setDetailRow(null)} sx={{ mt: -1, mr: -1 }}>
              <CloseIcon />
            </IconButton>
          </Stack>

          {detailRow && (
            <>
              <Box sx={{ borderTop: '1px solid #EDEDED', mt: 1 }}>
                <DetailRow label="Avm Adı" value={detailRow.avm} />
                <DetailRow label="Müşteri Adı" value={`${detailRow.name} ${detailRow.lastname}`} />
                <DetailRow label="Mağaza" value={detailRow.store} />
                <DetailRow label="İşlem Tarihi" value={detailRow.date} />
                <DetailRow label="Teslimat Saat Aralığı" value={detailRow.slot} />
                <DetailRow label="Statü" value={detailRow.status} />
                <DetailRow label="Onaylanma/Reddedilme Tarihi" value={detailRow.decidedAt} />
                <DetailRow label="Kurye Teslim Tarihi" value={detailRow.courierDeliveredAt} />
                <DetailRow label="Müşteriye Teslim Saati" value={detailRow.customerDeliveredAt} />
                <DetailRow label="Adres Bilgisi" value={detailRow.address} />
              </Box>

              <Typography sx={{ fontWeight: 700, mt: 3, mb: 1.5 }}>Fiş Görselleri</Typography>
              {detailRow.receipts.length === 0 ? (
                <Typography sx={{ color: '#667085', fontSize: 14 }}>Fiş görseli bulunamadı.</Typography>
              ) : (
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  {detailRow.receipts.map((receipt) => (
                    <Box key={receipt.name} sx={{ width: 76 }}>
                      <Box
                        component="img"
                        src={receipt.src}
                        alt={receipt.name}
                        onClick={() => setPreviewReceipt(receipt)}
                        sx={{
                          width: 76,
                          height: 96,
                          objectFit: 'cover',
                          border: '1px solid #EAECF0',
                          borderRadius: 1,
                          backgroundColor: '#FCFCFD',
                          cursor: 'pointer',
                        }}
                      />
                      <Typography sx={{ fontSize: 12, mt: 0.5 }}>{receipt.name}</Typography>
                    </Box>
                  ))}
                </Stack>
              )}

              {detailRow.isPending && (
                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 4 }}>
                  <Button
                    variant="contained"
                    disableElevation
                    sx={{ width: 180, height: 44, ...rejectButtonSx }}
                    onClick={() => requestAction(detailRow, 'reject')}
                  >
                    Reddet
                  </Button>
                  <Button
                    variant="contained"
                    disableElevation
                    sx={{ width: 180, height: 44, ...approveButtonSx }}
                    onClick={() => requestAction(detailRow, 'approve')}
                  >
                    Onayla
                  </Button>
                </Stack>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Onay / red öncesi soru */}
      <Dialog
        open={Boolean(pendingAction)}
        onClose={() => !acting && setPendingAction(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ p: 4 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {pendingAction?.type === 'approve' ? 'Talebi Onayla' : 'Talebi Reddet'}
          </Typography>
          <Typography sx={{ color: '#475467', mb: 3 }}>
            {pendingAction && `${pendingAction.row.name} ${pendingAction.row.lastname} adlı müşterinin talebini `}
            {pendingAction?.type === 'approve' ? 'onaylamak' : 'reddetmek'} istediğinize emin misiniz?
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <Button
              fullWidth
              variant="outlined"
              color="inherit"
              disabled={acting}
              onClick={() => setPendingAction(null)}
              sx={{ height: 44, color: '#344054' }}
            >
              Vazgeç
            </Button>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              disabled={acting}
              onClick={handleConfirmAction}
              sx={{ height: 44, ...(pendingAction?.type === 'approve' ? approveButtonSx : rejectButtonSx) }}
            >
              {acting ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                pendingAction?.type === 'approve' ? 'Evet, Onayla' : 'Evet, Reddet'
              )}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Fiş görseli önizleme */}
      <Dialog open={Boolean(previewReceipt)} onClose={() => setPreviewReceipt(null)} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => setPreviewReceipt(null)}
            sx={{ position: 'absolute', top: 8, right: 8, backgroundColor: 'rgba(255,255,255,0.9)' }}
          >
            <CloseIcon />
          </IconButton>
          {previewReceipt && (
            <Box
              component="img"
              src={previewReceipt.src}
              alt={previewReceipt.name}
              sx={{ display: 'block', width: '100%', maxHeight: '80vh', objectFit: 'contain', backgroundColor: '#F5F7FA' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
